import type { CareerApplicationDetail, CareerApplicationStatus, HrDashboard } from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Admin Careers (job applications / enquiries) API. Mirrors pages.api.ts: the
 * backend returns the standard `{ data, pagination }` list envelope. CV bytes
 * are fetched via a separate short-lived presigned URL, never inlined.
 */

export interface ApplicationListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  /** One status, or several comma-separated (multi-select filter). */
  status?: string;
  /** Exact role title. */
  positionTitle?: string;
  /** Rolling window in days: "7" | "30" | "90". */
  appliedWithin?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface ApplicationListResponse {
  data: CareerApplicationDetail[];
  pagination: { page: number; pageSize: number; totalItems: number };
}

const BASE = '/admin/careers/applications';

export const listApplications = async (
  query: ApplicationListQuery,
): Promise<ApplicationListResponse> => {
  const { data } = await apiClient.get<ApplicationListResponse>(BASE, { params: query });
  return data;
};

export const updateApplicationStatus = async (
  id: string,
  status: CareerApplicationStatus,
): Promise<CareerApplicationDetail> => {
  const { data } = await apiClient.patch<CareerApplicationDetail>(`${BASE}/${id}`, { status });
  return data;
};

export const deleteApplication = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${id}`);
};

/** Inclusive ISO date window. Omit both ends for all time. */
export interface HrDashboardRange {
  from?: string;
  to?: string;
}

/** Recruitment snapshot backing the HR dashboard. */
export const getHrDashboard = async (range?: HrDashboardRange): Promise<HrDashboard> => {
  const { data } = await apiClient.get<HrDashboard>(`${BASE}/dashboard`, {
    params: {
      ...(range?.from ? { from: range.from } : {}),
      ...(range?.to ? { to: range.to } : {}),
    },
  });
  return data;
};

/** Returns a short-lived presigned download URL for the applicant's CV. */
export const getCvUrl = async (id: string): Promise<{ url: string; fileName: string }> => {
  const { data } = await apiClient.get<{ url: string; fileName: string }>(`${BASE}/${id}/cv`);
  return data;
};
